import type {
	ApplicableInspectionItem,
	InspectionWithValues,
	ProfileInspectionsResult,
} from "../models/Inspection";
import type {
	TestShackReportItem,
	TestShackReportLegDelta,
} from "../models/TestShackReport";

/** What the Report and the Manager both know about a sample's spec. */
export interface InspectionToleranceSource {
	MinLbsPerFoot: number | null;
	MaxLbsPerFoot: number | null;
	items: Pick<TestShackReportItem, "Ltl" | "Utl">[];
	legDeltas: Pick<TestShackReportLegDelta, "LegDeltaMax">[];
}

/** False means the sample shows "No Specification" instead of "All in spec". */
export const hasInspectionTolerances = (
	inspection: InspectionToleranceSource,
): boolean =>
	inspection.MinLbsPerFoot !== null ||
	inspection.MaxLbsPerFoot !== null ||
	// Gauges carry Min/Max even though they are read as Go/No Go; Yes/No checks never do.
	inspection.items.some((item) => item.Ltl !== null || item.Utl !== null) ||
	inspection.legDeltas.some((delta) => delta.LegDeltaMax !== null);

/** Whether a Test Shack grid row can be judged; false paints nothing red. */
export const isInspectionEvaluated = (
	inspection: InspectionWithValues,
	applicableItemsBySpec:
		| ProfileInspectionsResult["applicableItemsBySpec"]
		| undefined,
	{ withLegDelta }: { withLegDelta: boolean },
): boolean => {
	const items: ApplicableInspectionItem[] | undefined =
		applicableItemsBySpec?.[inspection.ProfileSpecId];
	// Until the SP returns limits, keep judging readings as before.
	if (!items || items.some((item) => item.Ltl === undefined)) return true;
	return hasInspectionTolerances({
		MinLbsPerFoot: inspection.MinLbsPerFoot,
		MaxLbsPerFoot: inspection.MaxLbsPerFoot,
		items: items.map((item) => ({
			Ltl: item.Ltl ?? null,
			Utl: item.Utl ?? null,
		})),
		legDeltas: withLegDelta ? [{ LegDeltaMax: inspection.LegDeltaMax }] : [],
	});
};
